import { useEffect, useState } from 'react'
import { AccountBean, EventReportKeyColumnDescription, EventReportValueColumnDescription, KeyFilterClause } from 'src/api/api'
import { User, useUser } from 'src/context/UserContext'
import reportApi from '../report/ReportApi'

export interface Period {
    key: EventReportKeyColumnDescription.ColumnEnum
    dates: string[]
}

export interface TopCounters {
    name: string
    value: number
}

export interface TopData {
    key: EventReportKeyColumnDescription.ColumnEnum
    counters: TopCounters[]
}

export interface PerformanceData {
    dateTime: string[]
    impression: number[]
    click: number[]
    cost: number[]
    impressionSum: number
    clickSum: number
    costSum: number
    ctr: number[]
    ecpm: number[]
    ctrAvg: number
    ecpmAvg: number
}

export interface DashboardFilters {
    ssp: string[]
    mediaAgency: string[]
    advertiser: string[]
    campaign: string[]
    adGroup: string[]
    creative: string[]
    countryCode: string[]
}

const KeyColumnEnum = EventReportKeyColumnDescription.ColumnEnum
const ValueColumnEnum = EventReportValueColumnDescription.ColumnEnum
const OrderEnum = EventReportKeyColumnDescription.OrderEnum
const RoleEnum = AccountBean.RoleEnum

function buildFilterKeyColumns(filters?: DashboardFilters): EventReportKeyColumnDescription[] {

    const result: EventReportKeyColumnDescription[] = []

    if (!filters) return result

    if (filters.ssp.length > 0) result.push({
        column: KeyColumnEnum.SSP,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.ssp },
        onlyFilter: true
    })

    if (filters.mediaAgency.length > 0) result.push({
        column: KeyColumnEnum.MediaAgency,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.mediaAgency },
        onlyFilter: true
    })

    if (filters.advertiser.length > 0) result.push({
        column: KeyColumnEnum.Advertiser,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.advertiser },
        onlyFilter: true
    })

    if (filters.campaign.length > 0) result.push({
        column: KeyColumnEnum.Campaign,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.campaign },
        onlyFilter: true
    })

    if (filters.adGroup.length > 0) result.push({
        column: KeyColumnEnum.AdGroup,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.adGroup },
        onlyFilter: true
    })

    if (filters.creative.length > 0) result.push({
        column: KeyColumnEnum.Creative,
        where: { type: KeyFilterClause.TypeEnum.In, value: filters.creative },
        onlyFilter: true
    })

    return result
}

const getInitial = (user: User | undefined) => {
    switch (user?.role) {
        case RoleEnum.Admin: return [
            { key: KeyColumnEnum.SSP, counters: [] },
            { key: KeyColumnEnum.MediaAgency, counters: [] },
            { key: KeyColumnEnum.Advertiser, counters: [] }
        ]
        case RoleEnum.AgencyAdmin: return [
            { key: KeyColumnEnum.MediaAgency, counters: [] },
            { key: KeyColumnEnum.Advertiser, counters: [] },
            { key: KeyColumnEnum.Campaign, counters: [] }
        ]
        case RoleEnum.AgencyManager: return [
            { key: KeyColumnEnum.Advertiser, counters: [] },
            { key: KeyColumnEnum.Campaign, counters: [] },
            { key: KeyColumnEnum.AdGroup, counters: [] }
        ]
        default: return []
    }
}

export function useDashboardData(dates: Period, filters?: DashboardFilters) {

    const { user } = useUser()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(undefined)
    const [performanceData, setPerformanceData] = useState<PerformanceData>()

    const [topValuesData, setTopValuesData] = useState<TopData[]>(getInitial(user))

    const requestReport = async (keyColumns, valueColumns) => {
        try {
            const created = await reportApi.createReport({ keyColumns, valueColumns })
            return created.data
        } catch (e) {
            const message = await e.json()
            setError(message.error)
            return undefined
        }
    }

    const readReport = async (ready: Response) => {
        const blob = await ready.blob()
        const text = await blob.text()

        if (text.length < 1) return null

        const rows = text.split('\n')
        if (rows.length < 2) return null

        return rows
    }

    const loadPerformanceData = async () => {

        setLoading(true)

        const keyColumns: EventReportKeyColumnDescription[] = [
            {
                column: KeyColumnEnum.DayHour,
                where: {
                    type: KeyFilterClause.TypeEnum.Between,
                    value: dates.dates
                },
                onlyFilter: true
            },
            {
                column: dates.key,
                order: OrderEnum.Asc
            },
            ...buildFilterKeyColumns(filters)
        ]

        const valueColumns: EventReportValueColumnDescription[] = [
            { column: ValueColumnEnum.Impression },
            { column: ValueColumnEnum.Click },
            { column: ValueColumnEnum.Cost }
        ]

        const uuid = await requestReport(keyColumns, valueColumns)

        if (!uuid) {
            setLoading(false)
            return
        }

        const interval = setInterval(async () => {
            try {
                const ready = await reportApi.get(uuid ?? '')

                clearInterval(interval)

                const rows = await readReport(ready)
                if (rows == null) return

                rows.shift()
                rows.pop()

                const data = rows.map(row => row.split(','))

                let impressionSum = 0, clickSum = 0, costSum = 0

                const
                    dateTime: string[] = [],
                    impression: number[] = [],
                    click: number[] = [],
                    cost: number[] = [],
                    ctr: number[] = [],
                    ecpm: number[] = []

                for (const line of data) {

                    const dateTimeKey = line[0]
                    const impressionValue = parseInt(line[1])
                    const clickValue = parseInt(line[2])
                    const costValue = parseFloat(line[3])

                    const ctrValue = impressionValue ? clickValue * 100.0 / impressionValue : 0
                    const ecpmValue = impressionValue ? costValue / (impressionValue / 1000.0) : 0

                    impressionSum += impressionValue
                    clickSum += clickValue
                    costSum += costValue

                    dateTime.push(dateTimeKey)
                    impression.push(impressionValue)
                    click.push(clickValue)
                    cost.push(costValue)
                    ctr.push(ctrValue)
                    ecpm.push(ecpmValue)
                }

                const ctrAvg = impressionSum ? clickSum * 100.0 / impressionSum : 0
                const ecpmAvg = impressionSum ? costSum / (impressionSum / 1000.0) : 0

                setPerformanceData({
                    dateTime,
                    impression,
                    click,
                    cost,
                    impressionSum,
                    clickSum,
                    costSum,
                    ctr,
                    ecpm,
                    ctrAvg,
                    ecpmAvg
                })
                setLoading(false)
            } catch (err) {
                if (err.status !== 425) {
                    setLoading(false)
                    clearInterval(interval)
                }
            }
        }, 500)
    }

    const loadTopDataByColumn = async (
        keyColumn: EventReportKeyColumnDescription.ColumnEnum,
        valueColumn: EventReportValueColumnDescription.ColumnEnum,
        index: number
    ) => {

        setLoading(true)

        const eventReportKeyColumnDescriptions = buildFilterKeyColumns(filters)
        const keyColumns: EventReportKeyColumnDescription[] = [
            {
                column: KeyColumnEnum.DayHour,
                where: {
                    type: KeyFilterClause.TypeEnum.Between,
                    value: dates.dates
                },
                onlyFilter: true
            },
            {
                column: keyColumn,
                onlyFilter: false
            },
            ...eventReportKeyColumnDescriptions
        ]

        const valueColumns: EventReportValueColumnDescription[] = [
            {
                column: valueColumn,
                order: OrderEnum.Desc
            }
        ]

        const uuid = await requestReport(keyColumns, valueColumns)

        if (!uuid) {
            setLoading(false)
            return
        }

        const interval = setInterval(async () => {
            try {
                const ready = await reportApi.get(uuid ?? '')

                clearInterval(interval)

                const rows = await readReport(ready)
                if (rows == null) return

                rows.shift()
                rows.pop()

                const data = rows.map(row => row.split(','))

                const topData = data.map(line => {

                    const name = line[0]
                    const value = parseFloat(line[1])

                    return {
                        name,
                        value
                    }
                })

                const topByColumn = topValuesData[index]
                topByColumn.counters = topData

                const list = [...topValuesData]
                setTopValuesData(list)

                setLoading(false)
            } catch (err) {
                if (err.status !== 425) {
                    setLoading(false)
                    clearInterval(interval)
                }
            }
        }, 500)
    }

    const loadTopData = (keys: EventReportKeyColumnDescription.ColumnEnum[], value: EventReportValueColumnDescription.ColumnEnum) => {
        const loadingPromises = keys.map((key, index) => loadTopDataByColumn(key, value, index))
        return Promise.all(loadingPromises)
    }

    const loadData = async () => {
        await Promise.all([
            loadTopData(topValuesData.map(v => v.key), ValueColumnEnum.Cost),
            loadPerformanceData()
        ])
    }

    useEffect(() => {
        loadData()
    }, [dates, filters])

    return {
        loading,
        performanceData,
        topValuesData,
        loadPerformanceData,
        loadTopData
    }
}


// WEBPACK FOOTER //
// ./src/component/app/dashboard/useDashboardData.ts